import { t } from '@lingui/core/macro';
import { Plural, Trans } from '@lingui/react/macro';
import { useEffect, useRef, useState } from 'react';

import {
  appendAppBlockToMessages,
  appendStreamContentToMessages,
  type ChatMessageType,
  type UserChatMessageType,
} from '@/api/ai';
import AIStarsIcon from '@/assets/svg/ai-stars.svg?react';
import MessageIcon from '@/assets/svg/bx-message.svg?react';
import { Button } from '@/components/buttons';
import { AISearchInput } from '@/components/Form';
import { Combobox, ComboboxContent, ComboboxItem, ComboboxList } from '@/components/Form/pickers';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/Tooltip';
import { Text } from '@/components/typography';
import { usePageContext } from '@/hooks/page/PageContext';
import {
  WebSocketErrorCode,
  type WebSocketAIMessage,
  type WebSocketError,
  useAuthWebSocket,
} from '@/hooks/useAuthWebSocket';
import { reportMcpChartFailure } from '@/hooks/useAuthWebSocket/mcpChartObservability';
import { cn } from '@/lib/utils';

import { getAiChatErrorMessage, parseRateLimitRetryAfterSeconds } from './aiChatErrorMessage';
import AIDrawer from './AIDrawer';
import { getMcpChartRenderState } from './mcpChartHostUtils';
import { type RequestStatus } from './statusState';

const REQUEST_TIMEOUT_MS = 90_000;
const MAX_FINISHED_REQUEST_IDS = 100;
const ASSISTANT_FIELDS_CLASS = 'c99-assistant-fields';
const ASSISTANT_SURFACE_CLASS = 'c99-assistant-surface';

type AISearchProps = {
  compact?: boolean;
};

const AISearch = ({ compact = false }: AISearchProps) => {
  const { exampleAIQuestions } = usePageContext();
  const [chatSessionId, setChatSessionId] = useState<string>();
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessageType[]>([]);
  const [isAIResponseLoading, setAIResponseLoading] = useState(false);
  const [isAIStreamingResponse, setIsAIStreamingResponse] = useState(false);
  const [isRateLimited, setIsRateLimited] = useState(false);
  const [requestStatus, setRequestStatus] = useState<RequestStatus | undefined>(undefined);
  const inputRef = useRef<HTMLInputElement>(null);
  const inputGroupAnchorRef = useRef<HTMLDivElement>(null);
  const rateLimitTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isRequestActiveRef = useRef(false);
  const hasSentRequestRef = useRef(false);
  const activeRequestIdsRef = useRef(new Set<string>());
  const finishedRequestIdsRef = useRef(new Set<string>());

  const clearRequestTimeout = () => {
    activeRequestIdsRef.current.forEach((requestId) =>
      finishedRequestIdsRef.current.add(requestId),
    );
    activeRequestIdsRef.current.clear();
    while (finishedRequestIdsRef.current.size > MAX_FINISHED_REQUEST_IDS) {
      const oldestRequestId = finishedRequestIdsRef.current.values().next().value;
      if (oldestRequestId !== undefined) {
        finishedRequestIdsRef.current.delete(oldestRequestId);
      }
    }
    if (requestTimeoutRef.current != null) {
      clearTimeout(requestTimeoutRef.current);
      requestTimeoutRef.current = null;
    }
    isRequestActiveRef.current = false;
  };

  const failActiveRequest = (content: string) => {
    if (!isRequestActiveRef.current) {
      return;
    }

    clearRequestTimeout();
    setIsAIStreamingResponse(false);
    setAIResponseLoading(false);
    setRequestStatus(undefined);
    setMessages((prevMessages) => {
      const lastMessage = prevMessages[prevMessages.length - 1];
      const errorMessage: ChatMessageType = { content, role: 'error', sessionId: chatSessionId };

      if (lastMessage?.role === 'ai' && !lastMessage.isComplete) {
        return [...prevMessages.slice(0, -1), errorMessage];
      }

      return [...prevMessages, errorMessage];
    });
  };

  useEffect(() => {
    return () => {
      if (rateLimitTimeoutRef.current != null) {
        clearTimeout(rateLimitTimeoutRef.current);
      }
      if (requestTimeoutRef.current != null) {
        clearTimeout(requestTimeoutRef.current);
      }
    };
  }, []);

  const handleMessageReceived = (wsMessage: WebSocketAIMessage) => {
    if (
      !isRequestActiveRef.current ||
      (wsMessage.requestId && finishedRequestIdsRef.current.has(wsMessage.requestId))
    ) {
      return;
    }
    if (wsMessage.requestId) {
      activeRequestIdsRef.current.add(wsMessage.requestId);
    }

    if (wsMessage.type === 'complete') {
      clearRequestTimeout();
    }

    if (wsMessage.type === 'status') {
      setRequestStatus(wsMessage.payload);
      return;
    }

    if (wsMessage.type === 'app') {
      const renderState = getMcpChartRenderState({
        resourceUri: wsMessage.payload.resourceUri,
        structuredContent: wsMessage.payload.structuredContent,
      });

      if (!renderState.renderable) {
        if (renderState.reason === 'not_chartable') {
          reportMcpChartFailure({
            stage: 'not_chartable',
            resourceUri: wsMessage.payload.resourceUri,
            toolName: wsMessage.payload.toolName,
          });
        }

        return;
      }

      setMessages((prevMessages) =>
        appendAppBlockToMessages(prevMessages, {
          resourceUri: wsMessage.payload.resourceUri,
          toolName: wsMessage.payload.toolName,
          structuredContent: wsMessage.payload.structuredContent,
          requestId: wsMessage.requestId,
          sessionId: chatSessionId,
        }),
      );
      return;
    }

    if (wsMessage.type !== 'chunk') {
      setChatSessionId(wsMessage.payload.sessionId);
      setIsAIStreamingResponse(false);
    }

    setAIResponseLoading(false);
    setRequestStatus(undefined);
    setMessages((prevMessages) =>
      appendStreamContentToMessages(prevMessages, {
        content: wsMessage.payload.content,
        isComplete: wsMessage.type === 'complete',
        sessionId: wsMessage.type === 'chunk' ? chatSessionId : wsMessage.payload.sessionId,
        requestId: wsMessage.requestId,
      }),
    );
  };

  const applyRateLimitCooldown = (content: string) => {
    const retrySeconds = parseRateLimitRetryAfterSeconds(content);
    if (retrySeconds == null) {
      return;
    }

    if (rateLimitTimeoutRef.current != null) {
      clearTimeout(rateLimitTimeoutRef.current);
    }

    setIsRateLimited(true);
    rateLimitTimeoutRef.current = setTimeout(() => {
      setIsRateLimited(false);
      rateLimitTimeoutRef.current = null;
    }, retrySeconds * 1000);
  };

  const handleErrorReceived = (wsMessage: WebSocketError) => {
    if (
      (hasSentRequestRef.current && !isRequestActiveRef.current) ||
      (wsMessage.requestId && finishedRequestIdsRef.current.has(wsMessage.requestId))
    ) {
      return;
    }
    if (wsMessage.requestId) {
      activeRequestIdsRef.current.add(wsMessage.requestId);
    }

    clearRequestTimeout();
    setIsAIStreamingResponse(false);
    setAIResponseLoading(false);
    setRequestStatus(undefined);

    if (wsMessage.payload.code === WebSocketErrorCode.RATE_LIMIT_EXCEEDED) {
      applyRateLimitCooldown(wsMessage.payload.content);
    }

    setMessages((prevMessages) => {
      const lastMessage = prevMessages[prevMessages.length - 1];
      const errorMessage: ChatMessageType = {
        content: getAiChatErrorMessage(wsMessage),
        role: 'error',
        sessionId: wsMessage.payload.sessionId,
      };

      if (lastMessage?.role === 'ai' && !lastMessage.isComplete) {
        // if we were streaming and encountered an error, replace the accumulated streamed message
        // with the error message
        return [...prevMessages.slice(0, -1), errorMessage];
      }

      return [...prevMessages, errorMessage];
    });
  };

  const { onSendMessage, onRequestTimeout, connectionStatus } = useAuthWebSocket({
    onMessageReceived: handleMessageReceived,
    onErrorReceived: handleErrorReceived,
    onConnectionLost: () =>
      failActiveRequest(t`Connection lost. Please try again when reconnected.`),
  });

  const sendMessage = (message: string) => {
    if (connectionStatus !== 'Open' || isRequestActiveRef.current) {
      return false;
    }

    const sent = onSendMessage({
      content: message,
      sessionId: chatSessionId,
    });
    if (!sent) {
      return false;
    }
    hasSentRequestRef.current = true;

    const userMessage: UserChatMessageType = {
      content: message,
      role: 'user',
      sessionId: chatSessionId,
    };

    setMessages((prev) => [...prev, userMessage]);
    setAIResponseLoading(true);
    setIsAIStreamingResponse(true);
    setRequestStatus(undefined);
    isRequestActiveRef.current = true;
    const requestTimeout = setTimeout(() => {
      if (requestTimeoutRef.current !== requestTimeout) {
        return;
      }
      failActiveRequest(t`The response timed out. Please try again.`);
      onRequestTimeout();
    }, REQUEST_TIMEOUT_MS);
    requestTimeoutRef.current = requestTimeout;
    return true;
  };

  const handleSearch = (value: string) => {
    if (!sendMessage(value)) {
      return false;
    }
    setDrawerOpen(true);
    setOpen(false);
    setInputValue('');
    return true;
  };

  const handleSendMessage = (message: string) => sendMessage(message);

  const hasExampleQuestions = exampleAIQuestions.length > 0;

  return (
    <section
      className={cn(
        ASSISTANT_SURFACE_CLASS,
        '@container/assistant relative bg-gray-900 after:pointer-events-none after:absolute after:inset-0 after:rounded-[inherit] after:opacity-(--assistant-progress) after:shadow-lg',
        compact ? '[--assistant-default-progress:1]' : '[--assistant-default-progress:0]',
      )}
      aria-label={t`Ask Channel99`}
    >
      <div
        className={cn(
          ASSISTANT_FIELDS_CLASS,
          'flex items-stretch gap-4 [--assistant-expanded-padding-units:4] **:data-[align=inline-end]:mr-0 **:data-[align=inline-end]:h-full **:data-[align=inline-end]:gap-1 **:data-[align=inline-end]:py-0 **:data-[align=inline-end]:pr-0 **:data-[slot=input-group]:h-12 @max-2xl/assistant:gap-3 @max-2xl/assistant:[--assistant-expanded-padding-units:3] [&_[data-align=inline-end]>button]:h-full',
        )}
      >
        {messages.length === 0 ? (
          <div className="flex shrink-0 items-center gap-2">
            <AIStarsIcon aria-hidden="true" className="size-6 shrink-0" />
            <span className="@max-2xl/assistant:hidden">
              <Text variant="label" color="light-green" uppercase>
                <Trans>Ask Channel99</Trans>
              </Text>
            </span>
          </div>
        ) : (
          <Tooltip>
            <TooltipTrigger
              render={
                <Button
                  variant="ghost"
                  inverse
                  shape="square"
                  size="large"
                  className="h-12 gap-3 bg-gray-800 px-4 @max-2xl/assistant:w-12 @max-2xl/assistant:px-0"
                  aria-expanded={isDrawerOpen}
                  onClick={() => setDrawerOpen(true)}
                >
                  <span className="sr-only">
                    <Trans>Open Conversation</Trans>{' '}
                    <Plural value={messages.length} one="# message" other="# messages" />
                  </span>
                  <span className="relative flex shrink-0 items-center">
                    <MessageIcon aria-hidden="true" className="size-6 text-primary" />
                    <span
                      aria-hidden="true"
                      className="absolute -top-2 -right-2 flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 ring-2 ring-gray-800"
                    >
                      <Text variant="caption1" weight="semi-bold">
                        {messages.length}
                      </Text>
                    </span>
                  </span>
                  <span aria-hidden="true" className="@max-2xl/assistant:hidden">
                    <Text variant="label" color="light-green" uppercase>
                      <Trans>Open Conversation</Trans>
                    </Text>
                  </span>
                </Button>
              }
            />
            <TooltipContent>
              <Trans>Open Conversation</Trans>
            </TooltipContent>
          </Tooltip>
        )}
        {messages.length === 0 ? (
          <Combobox<string>
            items={exampleAIQuestions}
            filter={null}
            inputRef={inputRef}
            inputValue={inputValue}
            onInputValueChange={setInputValue}
            open={open && hasExampleQuestions}
            onOpenChange={setOpen}
            autoHighlight={false}
            highlightItemOnHover
            modal={false}
            onValueChange={(question) => {
              if (question) {
                handleSearch(question);
              }
            }}
          >
            <div ref={inputGroupAnchorRef} className="min-w-0 flex-1">
              <AISearchInput
                ref={inputRef}
                value={inputValue}
                onValueChange={setInputValue}
                className={cn(
                  'w-full',
                  'transition-[border-radius] delay-100 duration-100 ease-in-out',
                  {
                    'rounded-b-none delay-0 duration-50': open && hasExampleQuestions,
                  },
                )}
                size="large"
                showSendButton
                isSearchDisabled={
                  isAIStreamingResponse || isRateLimited || connectionStatus !== 'Open'
                }
                placeholder={t`Ask a question…`}
                aria-label={t`Ask Channel99`}
                autoComplete="off"
                onSearch={handleSearch}
                onFocus={() => setOpen(true)}
                onBlur={(event) => {
                  const nextElement = event.relatedTarget as HTMLElement | null;

                  if (nextElement?.closest('[data-slot="combobox-content"]')) {
                    return;
                  }

                  setOpen(false);
                }}
              />
            </div>

            <ComboboxContent
              anchor={inputGroupAnchorRef}
              side="bottom"
              align="start"
              sideOffset={-1}
              initialFocus={false}
              finalFocus={false}
              className={cn(
                'rounded-t-none rounded-b-2xl border border-green-500 p-1',
                'ring-1 ring-green-500/50 ring-offset-0',
              )}
            >
              <ComboboxList>
                {(question: string) => (
                  <ComboboxItem
                    key={question}
                    value={question}
                    className="text-sm aria-selected:font-normal data-highlighted:not-data-disabled:bg-gray-200"
                  >
                    {question}
                  </ComboboxItem>
                )}
              </ComboboxList>
            </ComboboxContent>
          </Combobox>
        ) : (
          <AISearchInput
            ref={inputRef}
            value={inputValue}
            className="min-w-0 flex-1"
            size="large"
            showSendButton
            isSearchDisabled={isAIStreamingResponse || isRateLimited || connectionStatus !== 'Open'}
            placeholder={t`Ask a follow-up…`}
            aria-label={t`Follow-up question`}
            autoComplete="off"
            onValueChange={setInputValue}
            onSearch={handleSearch}
          />
        )}
      </div>

      <AIDrawer
        open={isDrawerOpen}
        messages={messages}
        isAIResponseLoading={isAIResponseLoading}
        responseStatus={requestStatus}
        isAIStreamingResponse={isAIStreamingResponse}
        isSendDisabled={isRateLimited || connectionStatus !== 'Open'}
        onClose={() => setDrawerOpen(false)}
        onSendMessage={handleSendMessage}
      />
    </section>
  );
};

export default AISearch;
